import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';
import { IntranetTask, IntranetUser, TaskToggleResult } from '../types';

// Data for creating a new task
export interface CreateTaskData {
  company: string; // Account ID with name (e.g., "217 - eLink Design, Inc.")
  taskType: string; // INTERNAL, PROJECT, UPDATE, etc.
  description: string;
  projectId?: string;
  descriptionWhy?: string;
  billingCode?: string;
  entry?: 'START' | ''; // START to start immediately
}

interface CreateTaskResult {
  success: boolean;
  message: string;
}

interface CloseTaskResult {
  success: boolean;
  message: string;
}

interface UpdateTaskResult {
  success: boolean;
  message: string;
}

// Note/message attached to a task
export interface TaskNote {
  id: string;
  author: string;
  text: string;
  timestamp: string;
}

export interface TaskEditData {
  id: string;
  employeeId: string;
  accountId: string;
  projectId: string;
  completed: string;
  type: string;
  description: string;
  descriptionWhy: string;
  flaggedWhy: string;
  codeId: string;
  timeTotal: string;
  timeEst: string;
  dateClosed: string;
  dateDue: string;
  notes: TaskNote[];
  newNote?: string; // New note to add
  company?: string; // "accountId - accountName" string the intranet edit form requires
}

// Monday task data for estimate tracking
export interface MondayTaskData {
  id: string;
  name: string;
  boardName: string;
  boardId?: string;
  estimate: string | null;
  mondayUrl: string;
}

// Function types for hook actions
type RefetchFn = () => Promise<void>;
type ToggleTaskFn = (task: IntranetTask, mondayTaskData?: MondayTaskData) => Promise<TaskToggleResult | null>;
type CloseTaskFn = (task: IntranetTask) => Promise<CloseTaskResult | null>;
type CreateTaskFn = (taskData: CreateTaskData) => Promise<CreateTaskResult | null>;
type GetTaskForEditFn = (taskId: string) => Promise<TaskEditData | null>;
type UpdateTaskFn = (taskData: TaskEditData) => Promise<UpdateTaskResult | null>;

interface UseCurrentTasksResult {
  tasks: IntranetTask[];
  completedTasks: IntranetTask[];
  user: IntranetUser | null;
  loading: boolean;
  error: string | null;
  refetch: RefetchFn;
  toggleTask: ToggleTaskFn;
  closeTask: CloseTaskFn;
  createTask: CreateTaskFn;
  getTaskForEdit: GetTaskForEditFn;
  updateTask: UpdateTaskFn;
}

// Polling interval in milliseconds (1 minute - important for idle warning to detect running tasks)
const POLLING_INTERVAL = 60 * 1000;

// localStorage keys for caching
const CACHE_KEYS = {
  user: 'edoss_cache_user',
  tasks: 'edoss_cache_tasks',
  completedTasks: 'edoss_cache_completedTasks',
  timestamp: 'edoss_cache_timestamp'
};

// Hard cap on cached payload age (24h). The running-task elapsed time is
// derived from each task's wall-clock `timeStart` Unix timestamp, so showing
// cached tasks on refresh keeps the timer ticking from the real start time
// instead of resetting to 00:00:00 while the background fetch runs.
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000;

// Load cached data from localStorage
const loadCachedData = () => {
  try {
    const timestamp = localStorage.getItem(CACHE_KEYS.timestamp);
    if (!timestamp) return null;

    const age = Date.now() - parseInt(timestamp, 10);
    if (age > CACHE_MAX_AGE) return null;

    const user = localStorage.getItem(CACHE_KEYS.user);
    const tasks = localStorage.getItem(CACHE_KEYS.tasks);
    const completedTasks = localStorage.getItem(CACHE_KEYS.completedTasks);

    if (!user || !tasks || !completedTasks) return null;

    return {
      user: JSON.parse(user),
      tasks: JSON.parse(tasks),
      completedTasks: JSON.parse(completedTasks)
    };
  } catch {
    return null;
  }
};

// Save data to localStorage cache
const saveCacheData = (user: IntranetUser, tasks: IntranetTask[], completedTasks: IntranetTask[]) => {
  try {
    localStorage.setItem(CACHE_KEYS.user, JSON.stringify(user));
    localStorage.setItem(CACHE_KEYS.tasks, JSON.stringify(tasks));
    localStorage.setItem(CACHE_KEYS.completedTasks, JSON.stringify(completedTasks));
    localStorage.setItem(CACHE_KEYS.timestamp, Date.now().toString());
  } catch {
    // localStorage might be full or disabled
  }
};

/**
 * Hook to fetch current/active tasks for the logged-in developer from the intranet
 * Also fetches completed tasks for today
 * Automatically fetches the logged-in user's ID first
 * Polls every 2 minutes to keep data fresh (important for idle warning detection)
 * Uses localStorage caching for instant load with background refresh
 * @returns Object containing tasks, completed tasks, user info, loading state, error, and action functions
 */
export const useCurrentTasks = (): UseCurrentTasksResult => {
  // Initialize from cache for instant display
  const cachedData = loadCachedData();
  const [tasks, setTasks] = useState<IntranetTask[]>(cachedData?.tasks || []);
  const [completedTasks, setCompletedTasks] = useState<IntranetTask[]>(cachedData?.completedTasks || []);
  const [user, setUser] = useState<IntranetUser | null>(cachedData?.user || null);
  // If we have cached data, don't show loading spinner
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async (isPolling = false, isBackgroundRefresh = false) => {
    // Only show loading on initial fetch when no cache, not on polling or background refresh
    if (!isPolling && !isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      // First, get the logged-in user's ID
      const userResponse = await axios.get(`${API_URL}/intranet/me`, { withCredentials: true });
      const userData = userResponse.data;
      setUser(userData);

      if (!userData?.id) {
        setError('Could not get user ID');
        setTasks([]);
        setCompletedTasks([]);
        return;
      }

      // The legacy intranet uses PHP session locking; keep same-session calls sequential.
      const tasksResponse = await axios.get(`${API_URL}/intranet/tasks/${userData.id}`, { withCredentials: true });
      const completedResponse = await axios.get(
        `${API_URL}/intranet/tasks/${userData.id}/completed`,
        { withCredentials: true }
      );
      
      setTasks(tasksResponse.data);
      setCompletedTasks(completedResponse.data);
      
      // Update cache
      saveCacheData(userData, tasksResponse.data, completedResponse.data);
    } catch (err: any) {
      // Only set error on initial fetch, not polling failures
      if (!isPolling && !isBackgroundRefresh) {
        setError(err.response?.data?.error || 'Failed to fetch tasks');
        setTasks([]);
        setCompletedTasks([]);
      }
    } finally {
      if (!isPolling && !isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch - if we have cache, do background refresh; otherwise show loading
  useEffect(() => {
    const hasCachedData = !!loadCachedData();
    fetchTasks(false, hasCachedData);
  }, [fetchTasks]);

  // Polling for fresh data (important for idle warning to detect running tasks)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTasks(true);
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchTasks]);

  /**
   * Toggle a task's timer (start if stopped, stop if running)
   * @param task The task to toggle
   * @param mondayTaskData Optional Monday task data for estimate tracking
   * @returns The result with success status and whether it started or stopped
   */
  const toggleTask = useCallback(async (task: IntranetTask, mondayTaskData?: MondayTaskData): Promise<TaskToggleResult | null> => {
    if (!user?.id) return null;

    try {
      const response = await axios.post<TaskToggleResult>(
        `${API_URL}/intranet/tasks/toggle`,
        { 
          rawTask: task.rawTask,
          mondayTaskData: mondayTaskData || null
        },
        { withCredentials: true }
      );
      // Refetch tasks to get updated state
      await fetchTasks();
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle task');
      return null;
    }
  }, [user, fetchTasks]);

  /**
   * Close a task (mark as completed and remove from active tasks)
   * @param task The task to close
   * @returns The result with success status
   */
  const closeTask = useCallback(async (task: IntranetTask): Promise<CloseTaskResult | null> => {
    if (!user?.id) return null;

    try {
      const response = await axios.post<CloseTaskResult>(
        `${API_URL}/intranet/tasks/close`,
        { 
          employeeTaskId: task.id,
          employeeId: user.id,
          taskDescription: task.description,
          timeTotal: task.timeTotal,
          timeStart: task.timeStart
        },
        { withCredentials: true }
      );
      // Refetch tasks to remove the closed task from the list
      await fetchTasks();
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to close task');
      return null;
    }
  }, [user, fetchTasks]);

  /**
   * Create a new task on the intranet
   * @param taskData The task data to create
   * @returns The result with success status
   */
  const createTask = useCallback(async (taskData: CreateTaskData): Promise<CreateTaskResult | null> => {
    if (!user?.id) return null;

    try {
      const response = await axios.post<CreateTaskResult>(
        `${API_URL}/intranet/tasks/create`,
        { ...taskData, employeeId: user.id },
        { withCredentials: true }
      );
      
      // Store the result before refreshing tasks
      const result = response.data;
      
      // Refetch tasks to get the new task in the list
      // Don't let refresh failure affect the create result
      try {
        await fetchTasks(false, true);
      } catch {
        // Refresh failed, but task was created - ignore
      }
      
      return result;
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to create task';
      // Return a failure result so handleStart surfaces the real backend
      // message via the createError toast. Do NOT setError here — that renders
      // the full-page error screen with a Reload button, which is meant for
      // load/fetch failures, not create validation errors.
      return { success: false, message } as CreateTaskResult;
    }
  }, [user, fetchTasks]);

  /**
   * Get task details for editing
   * @param taskId The task ID to fetch
   * @returns The task data for editing
   */
  const getTaskForEdit = useCallback(async (taskId: string): Promise<TaskEditData | null> => {
    try {
      const response = await axios.get<TaskEditData>(
        `${API_URL}/intranet/tasks/${taskId}/edit`,
        { withCredentials: true }
      );
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch task details');
      return null;
    }
  }, []);

  /**
   * Update a task on the intranet
   * @param taskData The task data to update
   * @returns The result with success status
   */
  const updateTask = useCallback(async (taskData: TaskEditData): Promise<UpdateTaskResult | null> => {
    try {
      const response = await axios.post<UpdateTaskResult>(
        `${API_URL}/intranet/tasks/update`,
        taskData,
        { withCredentials: true }
      );
      // Refetch tasks to get the updated task
      await fetchTasks();
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update task');
      return null;
    }
  }, [fetchTasks]);

  // Wrap refetch to not pass the polling flag
  const refetch = useCallback(async () => {
    await fetchTasks(false);
  }, [fetchTasks]);

  return {
    tasks,
    completedTasks,
    user,
    loading,
    error,
    refetch,
    toggleTask,
    closeTask,
    createTask,
    getTaskForEdit,
    updateTask
  };
};

export default useCurrentTasks;
